import { FormDialog } from '@/components/FormDialog'
import { ImageUpload } from '@/components/ImageUpload'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useFileUploadField } from '@/hooks/useFileUpload'
import { isApiError } from '@/utils/error'
import { getMutationOptions } from '@/utils/mutation'
import { showError } from '@/utils/toast'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateFloor, useUpdateFloor } from '../hooks/useFloor'
import { Floor } from '../types/floor'

export function FloorDialog({
  open,
  onOpenChange,
  buildingId,
  floor,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  buildingId: string | null
  floor: Floor | null
}) {
  if (!buildingId) return
  const { t } = useTranslation()
  const createFloor = useCreateFloor()
  const updateFloor = useUpdateFloor()

  const [form, setForm] = useState({ index: 1, name: '', imageId: '' })
  const isInvalid = !form.index || !form.name || !form.imageId || !buildingId
  const isLoading = createFloor.isPending || updateFloor.isPending

  const imageField = useFileUploadField({
    value: form.imageId ? [form.imageId] : [],
    onChange: ids =>
      setForm(prev => ({
        ...prev,
        imageId: ids[0] || '',
      })),
    maxFiles: 1,
    onUploadError: error => {
      if (isApiError(error)) showError(error.message)
    },
  })

  const isEdit = !!floor

  useEffect(() => {
    if (floor) {
      setForm({
        index: floor.index,
        name: floor.name,
        imageId: floor.imageId,
      })
    } else {
      setForm({ index: 1, name: '1', imageId: '' })
    }
  }, [floor, open])

  function handleSubmit() {
    if (!form.index || !form.name || !form.imageId || !buildingId) return

    const options = getMutationOptions({
      successMessage: isEdit ? t('floorplan.floor.toast.editSuccess') : t('floorplan.floor.toast.addSuccess'),
      onSuccess: () => {
        onOpenChange(false)
      },
    })

    if (isEdit && floor) {
      updateFloor.mutate({ buildingId: buildingId, floorId: floor.id, payload: form }, options)
    } else {
      createFloor.mutate({ buildingId: buildingId, payload: form }, options)
    }
  }
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('floorplan.floor.edit') : t('floorplan.floor.add')}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      isInvalid={isInvalid}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('floorplan.floor.number')}</Label>
          <Input
            data-testid="floor-dialog-index"
            value={form.index}
            type="number"
            onChange={e => setForm({ ...form, index: Number(e.target.value) })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder="1"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('floorplan.floor.name')}</Label>
          <Input
            data-testid="floor-dialog-name"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder="1"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t('common.image')}</Label>
        {imageField.isUploading ? (
          <p className="text-[11px] text-muted-foreground">
            {t('common.uploadingImages')}
            {imageField.uploadProgress ? ` ${imageField.uploadProgress}%` : '...'}
          </p>
        ) : null}
        <ImageUpload
          files={imageField.files}
          imageIds={imageField.imageIds}
          onChange={files => imageField.handleChange(files)}
          onRemoveExisting={imageField.handleRemoveExisting}
          maxImages={1}
        />
      </div>
    </FormDialog>
  )
}
